import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { message } from 'antd'
import menusConfig from '@/components/AppHeader/menus'
import { fetchPermissionCodesByType, getJavaToken, getYs7Token, resetAppState } from '@/redux/app/appSlice'
import { mergeClientPermissionCodeLists } from '@/constants/menuPermissionMeta'
import { isMenuItemVisible, stripMenuRBACFields } from '@/utils/menuItemVisible'
import { clearAllAuth } from '@/utils/logout'
import { syncYingshiDevices, syncYingshiShareDevices } from '@/service/yingshi'
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'
import { fetchYingshiDeviceList } from '@/redux/yingshi/deviceListSlice'

const pathActive = (pathname, key) => {
  if (!key) return false
  if (pathname === key) return true
  return pathname.startsWith(`${key}/`)
}

const VisualScreenHeader = ({ storeId, onStoreIdChange }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user, browserType, token, permissionCodes, permissionCodesLoaded } = useSelector((state) => ({
    user: state.app.user,
    browserType: state.app.browserType,
    token: state.app.token,
    permissionCodes: state.app.permissionCodes,
    permissionCodesLoaded: state.app.permissionCodesLoaded
  }))
  const storeList = useSelector((state) => state.dashboard.storeList)
  const storeOption = Array.isArray(storeList?.default_storeOption) ? storeList.default_storeOption : []
  const mergedRbacCodes = useMemo(
    () => mergeClientPermissionCodeLists(permissionCodes),
    [permissionCodes]
  )
  const [currentTime, setCurrentTime] = useState('')
  const [currentDate, setCurrentDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [fetchingSync, setFetchingSync] = useState('')

  useEffect(() => {
    dispatch(fetchStoreList({ serviceType: 'default', dataType: 'storeOption' }))
  }, [dispatch])

  useEffect(() => {
    if (storeId || storeOption.length === 0) return
    const raw = user?.storeId != null && user.storeId !== '' ? user.storeId : user?.store_id
    const fromUser = raw != null && raw !== '' ? String(raw) : ''
    const hit = fromUser && storeOption.some((item) => String(item.id) === fromUser)
    onStoreIdChange(hit ? fromUser : String(storeOption[0].id))
  }, [storeOption, storeId, user, onStoreIdChange])

  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const timeStr = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
      const ms = String(now.getMilliseconds()).padStart(3, '0')
      setCurrentTime(`${timeStr}.${ms}`)
    }
    updateTime()
    const timer = setInterval(updateTime, 50)
    return () => clearInterval(timer)
  }, [])

  const visibleMenus = useMemo(() => {
    const walk = (items) => {
      if (!Array.isArray(items)) return []
      return items
        .filter((item) => isMenuItemVisible(item, mergedRbacCodes, permissionCodesLoaded))
        .filter((item) => item.key !== '/client/visualScreen')
        .map((item) => {
          const stripped = stripMenuRBACFields(item)
          if (!stripped) return null
          if (!item.children) return stripped
          const children = walk(item.children)
          if (children.length === 0) return null
          return { ...stripped, children }
        })
        .filter(Boolean)
    }
    return walk(menusConfig)
  }, [mergedRbacCodes, permissionCodesLoaded])

  const itemActive = (item) => {
    if (item.key === 'maternity' && pathname.includes('/visualScreen')) return true
    if (pathActive(pathname, item.key)) return true
    if (!item.children) return false
    return item.children.some(itemActive)
  }

  const go = (item) => {
    const key = item.key || (item.children && item.children[0] && item.children[0].key)
    if (typeof key !== 'string' || !key.startsWith('/')) return
    navigate(key)
    dispatch(fetchPermissionCodesByType())
  }

  const logout = useCallback(() => {
    clearAllAuth(token)
    dispatch(resetAppState())
    navigate('/system/login')
  }, [dispatch, navigate, token])

  const handleSyncYingshiDevices = async () => {
    if (fetchingSync) return
    setFetchingSync('direct')
    try {
      await syncYingshiDevices()
      if (storeId) {
        dispatch(fetchYingshiDeviceList({ query: { storeId: String(storeId).trim() } }))
      }
      message.success('同步设备成功')
    } catch (error) {
      const errMsg =
        (error && typeof error === 'object' && (error.msg || error.message)) || '同步设备失败'
      message.error(String(errMsg))
    } finally {
      setFetchingSync('')
    }
  }

  const handleSyncYingshiShareDevices = async () => {
    if (fetchingSync) return
    setFetchingSync('share')
    try {
      await syncYingshiShareDevices()
      if (storeId) {
        dispatch(fetchYingshiDeviceList({ query: { storeId: String(storeId).trim() } }))
      }
      message.success('同步分享设备成功')
    } catch (error) {
      const errMsg =
        (error && typeof error === 'object' && (error.msg || error.message)) || '同步分享设备失败'
      message.error(String(errMsg))
    } finally {
      setFetchingSync('')
    }
  }

  const userActions = [
    { key: 'logout', label: '退出登录', hide: browserType?.environment === 'dingtalk', onClick: logout },
    {
      key: 'alerts',
      label: '预警设置',
      menuCodes: ['service:alerts'],
      onClick: () => navigate('/client/serviceAlertsRulesList')
    },
    { key: 'ys', label: '萤石云 Token', menuCodes: ['service:ys'], onClick: () => dispatch(getYs7Token()) },
    { key: 'java', label: 'Java Token', menuCodes: ['service:java'], onClick: () => dispatch(getJavaToken()) },
    {
      key: 'sync',
      label: fetchingSync === 'direct' ? '同步设备中…' : '同步摄像头设备',
      menuCodes: ['service:java'],
      onClick: handleSyncYingshiDevices
    },
    {
      key: 'share',
      label: fetchingSync === 'share' ? '同步分享设备中…' : '同步分享摄像头设备',
      menuCodes: ['service:java'],
      onClick: handleSyncYingshiShareDevices
    }
  ].filter((item) => !item.hide && isMenuItemVisible(item, mergedRbacCodes, permissionCodesLoaded))

  const displayName = user?.name || '管理员'
  const avatarText = String(displayName).slice(0, 1)
  const selectedStore = storeOption.find((item) => String(item.id) === String(storeId))
  const storeLabel = selectedStore?.value || '门店'

  return (
    <header className="vs-hud">
      <div className="vs-hud__left">
        <div className="vs-hud__info">
          <span className="vs-hud__time">{currentTime}</span>
          <span className="vs-hud__online">● 在线</span>
        </div>
        <div className="vs-hud__filters">
          <div className="vs-hud__date-wrap">
            <div className="vs-hud__date">
              <span>{currentDate}</span>
              <span className="vs-hud__date-icon" aria-hidden="true" />
            </div>
            <input
              type="date"
              className="vs-hud__date-input"
              value={currentDate}
              onChange={(e) => setCurrentDate(e.target.value)}
            />
          </div>
          <div className="vs-hud__store-wrap">
            <button type="button" className="vs-hud__store">
              <span className="vs-hud__store-value">{storeLabel}</span>
              <span className="vs-hud__store-arrow">▼</span>
            </button>
            <div className="vs-hud__store-dropdown">
              {storeOption.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={[
                    'vs-hud__store-item',
                    String(item.id) === String(storeId) ? 'is-active' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => onStoreIdChange(String(item.id))}
                >
                  {item.value}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <nav className="vs-hud__nav">
        {visibleMenus.map((item) => (
          <div key={item.key || item.label} className="vs-hud__nav-item-wrap">
            <button
              type="button"
              className={['vs-hud__nav-item', itemActive(item) ? 'is-active' : ''].filter(Boolean).join(' ')}
              onClick={() => {
                if (item.children) return
                go(item)
              }}
            >
              <span className="vs-hud__nav-dot" />
              <span>{item.label}</span>
              {item.children ? <span className="vs-hud__nav-arrow">▾</span> : null}
            </button>
            {item.children ? (
              <div className="vs-hud__dropdown">
                {item.children.map((child) => (
                  <button
                    type="button"
                    key={child.key || child.label}
                    className={[
                      'vs-hud__dropdown-item',
                      pathActive(pathname, child.key) ? 'is-active' : ''
                    ].filter(Boolean).join(' ')}
                    onClick={() => go(child)}
                  >
                    <span className="vs-hud__dropdown-dot" />
                    {child.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </nav>

      <div className="vs-hud__right">
        <div className="vs-hud__user-wrap">
          <div className="vs-hud__user">
            {user?.iconpath ? (
              <img className="vs-hud__avatar vs-hud__avatar--img" src={user.iconpath} alt="" />
            ) : (
              <div className="vs-hud__avatar">{avatarText}</div>
            )}
            <span className="vs-hud__username">{displayName}</span>
            <span className="vs-hud__user-arrow">▾</span>
          </div>
          <div className="vs-hud__user-dropdown">
            {userActions.map((action) => (
              <button
                type="button"
                key={action.key}
                className="vs-hud__user-dropdown-item"
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  )
}

export default VisualScreenHeader
